import type { ReactNode } from 'react'
import type { CardVariant } from '../../../design/components'

/**
 * The sheet's sections, as one table.
 *
 * A character sheet is a dozen cards deep, and on a phone every one of them below the fold
 * costs a scroll to find and a tap to open. This is the vocabulary the quick-nav beside the
 * portrait renders from, and the ids the cards themselves carry — one list, so a nav entry
 * and its card can never drift apart.
 *
 * Nothing here decides *whether* a card renders, or whether it has anything in it. Each card
 * keeps its own rules for both — an equipment card with no items and no right to add is not
 * shown at all, and one shown only so its owner can add to it says so with `data-empty`. The
 * nav reads the page for both answers rather than restating them; see {@link SheetNav}.
 *
 * Aspects is deliberately absent. It is the first card on the sheet and it never collapses,
 * so a button that scrolls to the top of the page and opens nothing is a button that does
 * nothing.
 */
export type SheetSectionKey =
  | 'skills'
  | 'harm'
  | 'fate'
  | 'powers'
  | 'stunts'
  | 'specializations'
  | 'focusItems'
  | 'enchantedItems'
  | 'roteSpells'
  | 'equipment'
  | 'bio'

/**
 * The element id each card carries.
 *
 * Prefixed because these are ids in the *document*, not in a module: the sheet shares the
 * page with the shell, the status bar and the assistant dock.
 */
export const SHEET_SECTION_ID: Record<SheetSectionKey, string> = {
  skills: 'sheet-skills',
  harm: 'sheet-harm',
  fate: 'sheet-fate',
  powers: 'sheet-powers',
  stunts: 'sheet-stunts',
  specializations: 'sheet-specializations',
  focusItems: 'sheet-focus-items',
  enchantedItems: 'sheet-enchanted-items',
  roteSpells: 'sheet-rote-spells',
  equipment: 'sheet-equipment',
  bio: 'sheet-bio',
}

export interface SheetSection {
  key: SheetSectionKey
  id: string
  /**
   * The word on the button from 900px up, and the start of its accessible name below that,
   * where only the icon shows. Short on purpose — "Stress", not "Stress & consequences" —
   * because a dozen of these share one row beside a 120px portrait.
   */
  label: string
  /**
   * The card this button leads to — which is also its colour. The nav draws each glyph in
   * `--section-<variant>`, the same code the card's left spine carries (tokens.css), so the
   * button and the thing it scrolls to are recognisably one section. The four spellcasting
   * cards share `magic` here exactly as they share it there.
   */
  variant: CardVariant
  icon: ReactNode
}

/** A 24px stroke glyph in the house style — same geometry as the header's nav icons. */
function glyph(paths: ReactNode): ReactNode {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.6"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      focusable="false"
    >
      {paths}
    </svg>
  )
}

/**
 * The three sections the nav offers even when they have nothing in them.
 *
 * Everywhere else an empty card is not worth a button: jumping to "None yet." tells a reader
 * something they could have learned from the button not being there. These three are the
 * exception because they are the sheet's working surface rather than a list of things a
 * character happens to own — skills and the stress tracks are played from at every table, and
 * equipment is where you go to record the first thing you pick up.
 */
export const ALWAYS_OFFERED: SheetSectionKey[] = ['skills', 'harm', 'equipment']

/**
 * Every section, in the order the sheet renders them — the nav is a map of the page, so it
 * has to read down in the order the page does.
 */
export const SHEET_SECTIONS: SheetSection[] = [
  {
    key: 'skills',
    id: SHEET_SECTION_ID.skills,
    variant: 'skills',
    label: 'Skills',
    // The skill pyramid, as rungs.
    icon: glyph(<path d="M5 19h14M6.5 15.5h11M8 12h8M9.5 8.5h5M11 5h2" />),
  },
  {
    key: 'harm',
    id: SHEET_SECTION_ID.harm,
    variant: 'harm',
    label: 'Stress',
    // A heart with a crack through it — stress and consequences, not health.
    icon: glyph(
      <>
        <path d="M12 20.2 4.9 13a4.4 4.4 0 0 1 6.2-6.2l.9.9.9-.9A4.4 4.4 0 0 1 19.1 13z" />
        <path d="M12.8 9.4 11 12.3h2.2l-1.6 2.6" />
      </>,
    ),
  },
  {
    key: 'fate',
    id: SHEET_SECTION_ID.fate,
    variant: 'fate',
    label: 'Fate',
    // A fate token: the chip you push across the table.
    icon: glyph(
      <>
        <circle cx="12" cy="12" r="8.5" />
        <path d="M12 7.8l1.2 2.9 3 .3-2.3 2 .7 2.9-2.6-1.6-2.6 1.6.7-2.9-2.3-2 3-.3z" />
      </>,
    ),
  },
  {
    key: 'powers',
    id: SHEET_SECTION_ID.powers,
    variant: 'powers',
    label: 'Powers',
    icon: glyph(
      <>
        <path d="M12 3.5c3.2 3.4 5 6 5 8.6a5 5 0 0 1-10 0c0-1.7.6-3.2 1.8-4.6.2 1.6 1 2.4 1.7 2.4 1.1 0 1.6-1.2 1-2.8a13 13 0 0 0-.5-1.3z" />
      </>,
    ),
  },
  {
    key: 'stunts',
    id: SHEET_SECTION_ID.stunts,
    variant: 'stunts',
    label: 'Stunts',
    icon: glyph(<path d="M13.5 3 6 13.5h5L10.5 21 18 10.5h-5z" />),
  },
  {
    key: 'specializations',
    id: SHEET_SECTION_ID.specializations,
    variant: 'magic',
    label: 'Specializations',
    // Crosshairs: points spent to aim one kind of magic.
    icon: glyph(
      <>
        <circle cx="12" cy="12" r="7.5" />
        <circle cx="12" cy="12" r="2.5" />
        <path d="M12 2.5v3M12 18.5v3M2.5 12h3M18.5 12h3" />
      </>,
    ),
  },
  {
    key: 'focusItems',
    id: SHEET_SECTION_ID.focusItems,
    variant: 'magic',
    label: 'Focus items',
    // A staff with a spark at its tip.
    icon: glyph(
      <>
        <path d="M4 20 14.5 9.5" />
        <path d="M13 7.5 16.5 11" />
        <path d="M18.5 3v4M16.5 5h4" />
      </>,
    ),
  },
  {
    key: 'enchantedItems',
    id: SHEET_SECTION_ID.enchantedItems,
    variant: 'magic',
    label: 'Enchanted items',
    // An amulet: a stone on a band.
    icon: glyph(
      <>
        <path d="M8 3.5c1.5 2 2.5 3.5 2.5 5M16 3.5c-1.5 2-2.5 3.5-2.5 5" />
        <circle cx="12" cy="14" r="5.5" />
        <path d="M12 11.2 13 13.4l2.3.2-1.8 1.6.6 2.3-2.1-1.3-2.1 1.3.6-2.3-1.8-1.6 2.3-.2z" />
      </>,
    ),
  },
  {
    key: 'roteSpells',
    id: SHEET_SECTION_ID.roteSpells,
    variant: 'magic',
    label: 'Rote spells',
    // A scroll: a rote is the spell you wrote down so you never have to improvise it.
    icon: glyph(
      <>
        <path d="M6.5 3.5h11v14a3 3 0 0 0 3 3h-11a3 3 0 0 1-3-3z" />
        <path d="M9.5 7.5h5M9.5 11h5M9.5 14.5h3" />
      </>,
    ),
  },
  {
    key: 'equipment',
    id: SHEET_SECTION_ID.equipment,
    variant: 'equipment',
    label: 'Equipment',
    // A satchel — the sheet's gear is a kit, not a weapon rack.
    icon: glyph(
      <>
        <path d="M5 8.5h14l-1 12H6z" />
        <path d="M9 8.5V6.5a3 3 0 0 1 6 0v2" />
      </>,
    ),
  },
  {
    key: 'bio',
    id: SHEET_SECTION_ID.bio,
    variant: 'bio',
    label: 'Bio',
    icon: glyph(
      <>
        <circle cx="12" cy="8" r="3.5" />
        <path d="M5 20c0-3.6 3.1-6 7-6s7 2.4 7 6" />
      </>,
    ),
  },
]

/** Smooth unless the reader has asked the system for less movement (spec §3.4, §8). */
function scrollBehavior(): ScrollBehavior {
  if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return 'auto'
  return window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth'
}

/**
 * Take the reader to a section: open it if it is collapsed, then scroll it under the header.
 *
 * Opening is `details.open = true` rather than React state because the collapsible cards are
 * built on `<details>` — the platform owns that flag, and `CollapsibleCard`'s own header
 * action already sets it the same way. Cards clear the sticky header via `scroll-margin-top`
 * (Card.module.css), so `block: 'start'` lands on the label rather than behind it.
 *
 * A missing id is not an error: a card that is not on this sheet is simply not navigable, and
 * the nav does not offer it.
 */
export function goToSheetSection(id: string): void {
  const card = document.getElementById(id)
  if (card === null) return

  const details = card.querySelector('details')
  if (details !== null) details.open = true

  card.scrollIntoView({ behavior: scrollBehavior(), block: 'start' })
}
